import type { AuditAction, AuditEntry } from '@avrahami/contracts';
import type { ReactNode } from 'react';
import { Cell, Table } from './components';
import { formatDateTime, shortId } from './format';

export interface AuditWords {
  when: string;
  action: string;
  actor: string;
  resource: string;
  details: string;
  system: string;
  anonymous: string;
}

function actorOf(entry: AuditEntry, words: AuditWords): string {
  if (entry.actorKind === 'SYSTEM') return words.system;
  if (entry.actorUserId === null) return words.anonymous;
  return shortId(entry.actorUserId);
}

/** Metadata as `key: value` pairs (it holds safe, non-secret values only). */
export function auditDetails(entry: AuditEntry): string {
  return Object.entries(entry.metadata)
    .map(([key, value]) => `${key}: ${String(value)}`)
    .join(' · ');
}

/**
 * Audit rows, newest first. `extraColumn` adds one column after the action (the platform admin
 * names the tenant there).
 */
export function AuditEntriesTable({
  entries,
  actionLabels,
  words,
  extraColumn,
  timeZone,
}: {
  entries: readonly AuditEntry[];
  actionLabels: Readonly<Record<AuditAction, string>>;
  words: AuditWords;
  extraColumn?: { label: string; render: (entry: AuditEntry) => ReactNode } | undefined;
  /** The zone the times are shown in (the browser's when absent). */
  timeZone?: string | undefined;
}) {
  const head = [
    words.when,
    words.action,
    ...(extraColumn === undefined ? [] : [extraColumn.label]),
    words.actor,
    words.resource,
    words.details,
  ];
  return (
    <Table head={head}>
      {entries.map((entry) => (
        <tr key={entry.id} data-testid="audit-row" data-action={entry.action}>
          <Cell className="whitespace-nowrap">{formatDateTime(entry.occurredAt, timeZone)}</Cell>
          <Cell>{actionLabels[entry.action]}</Cell>
          {extraColumn === undefined ? null : (
            <Cell className="ltr">{extraColumn.render(entry)}</Cell>
          )}
          <Cell className="ltr font-mono text-xs">
            <span title={entry.actorUserId ?? undefined}>{actorOf(entry, words)}</span>
          </Cell>
          <Cell className="text-xs text-slate-600">
            {entry.resourceType}
            {entry.resourceId === null ? null : (
              <span className="ltr ms-1 font-mono" title={entry.resourceId}>
                {shortId(entry.resourceId)}
              </span>
            )}
          </Cell>
          <Cell className="ltr max-w-xs text-xs break-words text-slate-600">
            {auditDetails(entry)}
          </Cell>
        </tr>
      ))}
    </Table>
  );
}
